import {
  IBenefit,
  IBenefitEnrollmentItem,
  IBenefitListTable,
  IBenefitType,
  IBenefitsReview,
  ICartBenefit,
  IEnrollmentEvent,
} from 'src/interfaces';
import { AxiosResponse } from 'axios';
import { getStorageValue } from 'src/hooks/useLocalStorage';
import http from './http';

export interface IEnrollmentEventsWithPagination {
  [x: string]: string | number | boolean | undefined;
  page: number;
  limit: number;
}

export interface IAvailableBenefitsParams {
  [planDescription: string]: string;
}

export interface IEnrollmentPost {
  added: IBenefitEnrollmentItem[];
  removed: string[];
  summary?: IBenefitsReview[];
}

export interface ICreateBeneficiarieParams {
  relationship: string;
  firstName: string;
  lastName: string;
  homePhone: string;
  workPhone: string;
  birthDate: string;
  gender: string;
  email: string;
  ssn: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  county: string;
  student: string;
  smoker: string;
}

class BenefitsService {
  static enrollForOpenEnrollment(data: IEnrollmentPost) {
    return http.post('/benefits/open-enrollment', data).then(response => response.data);
  }

  static enrollForNewHire(data: IEnrollmentPost) {
    return http.post('/benefits/new-hire', data).then(response => response.data);
  }

  static enrollForUpdateEnrollment(data: IEnrollmentPost) {
    return http.post('/benefits/update-enrollment', data).then(response => response.data);
  }

  static enrollForQle(data: FormData) {
    return http.post('/benefits/qle', data).then(response => response.data);
  }

  static getFreePlans(event: string) {
    return http.get(`/benefits/free-plans?eventType=${event}`).then(response => response.data);
  }

  static getBenefits(): Promise<{
    employeeCurrentBenefits: IBenefit[];
    totalDeduction: number;
    isNewHire: boolean;
    isQle: boolean;
    isBenefitUpdate: boolean;
    isOpenEnrollment: boolean;
    isEnrollmentOpen: boolean;
    daysToEndMonth: number;
    employeeLastBenefitDto: IBenefit[];
    categoriesToElect: string[];
    freePlansAvailable?: boolean;
  }> {
    return http.get('/benefits').then(response => response.data);
  }

  // eslint-disable-next-line no-unused-vars
  static getBenefitsDeductions(
    personnelNumber: string,
    params: any
  ): Promise<{ items: IBenefitListTable[]; meta: { totalItems: number } }> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }
    return http
      .get(`benefits/deductions/${personnelNumber}?${stringParams}`, {
        headers: { companyId: params.companyId },
      })
      .then(resp => resp.data);
  }

  static getBenefitTypes(eventType?: string): Promise<IBenefitType[]> {
    return http.get(`/benefits/types/${eventType || 'others'}`).then(response => response.data);
  }

  static getAvailableBenefits(params: IAvailableBenefitsParams): Promise<IBenefit[]> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `?${key}=${encodeURIComponent(params[key] as string)}`;
      } else {
        stringParams += `&${key}=${encodeURIComponent(params[key] as string)}`;
      }
    }
    return http.get(`/benefits/available${stringParams}`).then(response => response.data);
  }

  static getAvailablePlanDescriptionByType(
    benefitType: string,
    eventType: string
  ): Promise<{ planDescription: string; imgURL: string; enableSmokeOption: boolean }[]> {
    return http.get(`/benefits/available/${benefitType}/${eventType}`).then(response => response.data);
  }

  static getPendingBenefits(): Promise<IBenefit[]> {
    return http.get('/benefits/pending').then(response => response.data);
  }

  static getBenefitsSummaryHistory(params: any): Promise<{ items: IBenefitsReview[]; meta: { totalItems: number } }> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }
    return http
      .get(`benefits/summaries?${stringParams}`, {
        headers: { companyId: params.companyId },
      })
      .then(resp => resp.data);
  }

  static getMandatoryBenefits(): Promise<IBenefit[]> {
    return http.get('/benefits/mandatory').then(response => response.data);
  }

  static getEnrollmentEvents(params: IEnrollmentEventsWithPagination): Promise<{
    data: { items: IEnrollmentEvent[]; meta: { totalItems: number } };
  }> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }
    return http.get(`benefits/enrollment-events?${stringParams}`);
  }

  static createBeneficiary(params: ICreateBeneficiarieParams): Promise<AxiosResponse | any> {
    return http.post('benefits/create-beneficiarie', params);
  }

  static addBeneficiarie(params: any) {
    http.post('benefits/add-beneficiaries', params);
  }

  static updateCartSession(cart: ICartBenefit[], eventType?: string) {
    const eventQuery = eventType ? `?eventType=${encodeURIComponent(eventType)}` : '';
    return http.post(`benefits/cart-session${eventQuery}`, cart);
  }

  static getCartSession(eventType?: string): Promise<{ cartBody: ICartBenefit[] }> {
    const eventQuery = eventType ? `?eventType=${encodeURIComponent(eventType)}` : '';
    return http.get(`benefits/cart-session${eventQuery}`).then(response => response.data);
  }

  static clearCartSession(eventType?: string) {
    const eventQuery = eventType ? `?eventType=${encodeURIComponent(eventType)}` : '';
    return http.get(`benefits/cart-session-clear${eventQuery}`);
  }

  static planDocDownload(versionId: string, planDocument: string, type: string) {
    return `${
      process.env.REACT_APP_API_URL
    }plan-documents/download?versionId=${versionId}&planDocument=${planDocument}&type=${type}&token=${getStorageValue(
      'accessToken',
      null
    )}`;
  }
}
export default BenefitsService;
